import { FC, useState } from 'react'
import { CircularProgress } from '@material-ui/core'
import gql from 'graphql-tag'
import styled from 'styled-components'
import { Close } from 'svgIcons'
import { useModalRecommendTagsQuery } from 'generated/graphql'
import Button from '@app.components/Button'
import Modal from 'app.components/Modal/Modal'
import { CustomChip } from './Chip'
import RecommendTags, { RecommendTag } from './RecommendTags'

interface IFModalRecommendTagsProps {
  onClose: () => void
  onApply: (tags: string[]) => void
}

gql`
  fragment ModalRecommendTags_tag on Tag {
    id
    name
  }
  fragment ModalRecommendTags_tagConnection on TagConnection {
    nodes {
      ...ModalRecommendTags_tag
    }
  }
  fragment ModalRecommendTags_tagCollection on TagCollection {
    id
    title
    tags {
      ...ModalRecommendTags_tagConnection
    }
  }

  query ModalRecommendTags {
    recommendNewsItemTagCollection {
      ...ModalRecommendTags_tagCollection
    }
  }
`

const ModalRecommendTags: FC<IFModalRecommendTagsProps> = ({
  onClose,
  onApply,
}) => {
  const { data, loading } = useModalRecommendTagsQuery()

  const [tagList, setTagList] = useState<string[]>([])

  const handleTagClick = (tag: RecommendTag) => {
    setTagList((prev) => [...prev, tag.name])
  }

  const handleTagDelete = (index: number) => {
    setTagList((prev) => {
      const newTagList = [...prev]
      newTagList.splice(index, 1)
      return newTagList
    })
  }

  const handleApplyButtonClick = () => {
    onApply(tagList)
    onClose()
  }

  return (
    <Modal onClose={onClose} open>
      <StyledWrapper>
        <div className="recommend-header">
          <div>추천태그 선택</div>
          <Close className="close-button" onClick={onClose} />
        </div>
        <div className="recommend-content">
          <div className="head">
            *연관있는 태그를 추가해 소식을 더 효과적으로 노출하세요.
          </div>
          {tagList.length > 0 && (
            <div className="tag-list-wrapper">
              {tagList.map((tag, tagIndex) => (
                <CustomChip
                  key={`recommend-tag-item-${tagIndex}`}
                  color="primary"
                  label={`#${tag}`}
                  onDelete={() => {
                    handleTagDelete(tagIndex)
                  }}
                />
              ))}
            </div>
          )}
          {loading ? (
            <div className="loading-wrapper">
              <CircularProgress />
            </div>
          ) : (
            <div className="tag-group-wrapper">
              {data?.recommendNewsItemTagCollection?.map((tagCollection) => {
                if (tagCollection.tags.nodes.length === 0) {
                  return null
                }
                return (
                  <div key={`tag-group-${tagCollection.title}`}>
                    <div className="title">{tagCollection.title}</div>
                    <RecommendTags
                      tags={tagCollection.tags.nodes.map((node) => ({
                        name: node.name,
                      }))}
                      onClickTag={handleTagClick}
                    />
                  </div>
                )
              })}
            </div>
          )}
        </div>
        <div className="recommend-footer">
          <Button
            color="primary"
            onClick={handleApplyButtonClick}
            disabled={tagList.length === 0}
            className="button"
          >
            적용
          </Button>
        </div>
      </StyledWrapper>
    </Modal>
  )
}

export default ModalRecommendTags

const StyledWrapper = styled.div`
  width: 100%;
  max-width: 700px;
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  background-color: #fff;
  border-radius: 15px;

  .recommend-header {
    position: relative;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 32px;
    font-size: 24px;
    font-weight: 700;
    border-bottom: 1px solid #dddddd;
  }

  .close-button {
    position: absolute;
    top: 32px;
    right: 32px;
    width: 16px;
    height: 16px;
    :hover {
      cursor: pointer;
    }
  }

  .tag-list-wrapper {
    display: flex;
    gap: 8px;
    flex-wrap: wrap;
    border: 1px solid #dddddd;
    border-radius: 5px;
    margin-top: 12px;
    padding: 20px;
    width: 100%;
    max-height: 213px;
    overflow-y: scroll;
  }

  .recommend-content {
    padding: 40px;

    max-height: 600px;
    overflow-y: scroll;

    .head {
      font-size: 20px;
      font-weight: 700;
    }

    .loading-wrapper {
      display: flex;
      align-items: center;
      justify-content: center;
      margin-top: 32px;
    }

    .tag-group-wrapper {
      display: flex;
      flex-direction: column;
      gap: 28px;
      margin-top: 32px;

      .title {
        font-size: 16px;
        font-weight: 500;
        margin-bottom: 10px;
      }
    }
  }

  .recommend-footer {
    display: flex;
    align-items: center;
    justify-content: center;
    border-top: 1px solid #dddddd;
    padding: 20px 32px 32px;
    .button {
      width: 87px;
      height: 48px;
      font-size: 16px;
      color: #fff;
    }
  }
`
